import OLCesium, { VectorSynchronizer } from 'olcs';
import { unByKey } from 'ol/Observable';

import CesiumLayerFactory from '../factories/CesiumLayerFactory';

/**
 * 3D counterpart of the Map model: wraps an OLCesium instance synchronized
 * with the map's ol/Map and manages the Cesium-only pieces (imagery mirror,
 * terrain, 3D tileset).
 *
 * Only the olcs VectorSynchronizer is used — the RasterSynchronizer does not
 * support view projections beyond EPSG:4326/3857 (ours are e.g. EPSG:3763),
 * so raster layers are mirrored manually as Cesium imagery layers built by
 * CesiumLayerFactory from the same backend configs. Visibility/opacity keep
 * following the OL layers through listeners, so the existing LayerSwitcher
 * also drives the 3D view.
 */
class Map3D {
  /**
   * @param {Map} map The models/Map instance to mirror (its olMap drives the
   * camera and vector synchronization).
   * @param {object} [options]
   * @param {string} [options.terrainUrl] Base URL of a Cesium quantized-mesh
   * tileset (directory containing layer.json). Empty: default ellipsoid.
   * @param {string} [options.tilesetUrl] URL of a 3D Tiles tileset.json.
   */
  constructor(map, { terrainUrl, tilesetUrl } = {}) {
    this.map = map;
    this.destroyed = false;
    this.tileset = null;
    this.listenerKeys = [];

    this.olcs = new OLCesium({
      map: map.olMap,
      createSynchronizers: (olMap, scene) => [new VectorSynchronizer(olMap, scene)],
    });
    this.scene = this.olcs.getCesiumScene();

    this.mirrorRasterLayers();

    if (terrainUrl) {
      this.loadTerrain(terrainUrl);
    }

    if (tilesetUrl) {
      this.loadTileset(tilesetUrl);
    }
  }

  /**
   * Builds one Cesium imagery layer per raster layer of the map (in the map's
   * layer order) and keeps show/alpha in sync with the OL layer.
   */
  mirrorRasterLayers() {
    const Cesium = CesiumLayerFactory.getCesium();

    this.map.layers.forEach((layer) => {
      const provider = CesiumLayerFactory.createImageryProvider({
        sourceType: layer.sourceType,
        url: layer.serviceUrl,
        wmsParams: layer.wmsParams,
      });

      if (!provider) {
        return;
      }

      const imageryLayer = new Cesium.ImageryLayer(provider, {
        show: layer.olLayer.getVisible(),
        alpha: layer.olLayer.getOpacity(),
      });
      this.scene.imageryLayers.add(imageryLayer);

      this.listenerKeys.push(
        layer.olLayer.on('change:visible', () => {
          imageryLayer.show = layer.olLayer.getVisible();
        }),
        layer.olLayer.on('change:opacity', () => {
          imageryLayer.alpha = layer.olLayer.getOpacity();
        }),
      );
    });
  }

  loadTerrain(terrainUrl) {
    const Cesium = CesiumLayerFactory.getCesium();

    Cesium.CesiumTerrainProvider.fromUrl(terrainUrl)
      .then((terrainProvider) => {
        if (this.destroyed) {
          return;
        }
        this.scene.terrainProvider = terrainProvider;
        this.scene.globe.depthTestAgainstTerrain = true;
      })
      .catch((error) => {
        console.error(`Erro ao carregar o terreno de ${terrainUrl}:`, error);
      });
  }

  loadTileset(tilesetUrl) {
    const Cesium = CesiumLayerFactory.getCesium();

    Cesium.Cesium3DTileset.fromUrl(tilesetUrl)
      .then((tileset) => {
        if (this.destroyed) {
          return;
        }
        this.tileset = this.scene.primitives.add(tileset);
      })
      .catch((error) => {
        console.error(`Erro ao carregar o tileset 3D de ${tilesetUrl}:`, error);
      });
  }

  /**
   * Flies the camera to the 3D tileset, if one is loaded.
   */
  flyToTileset() {
    if (this.tileset) {
      this.scene.camera.flyToBoundingSphere(this.tileset.boundingSphere, { duration: 1.5 });
    }
  }

  /**
   * Shows/hides the 3D view (synchronizing the camera on every switch).
   * @param {boolean} enabled
   */
  setEnabled(enabled) {
    this.olcs.setEnabled(enabled);
  }

  getEnabled() {
    return this.olcs.getEnabled();
  }

  /**
   * Releases everything. Must be called when the map is rebuilt (e.g. the
   * ResetControl refresh path re-creates the Map model) or on unmount.
   */
  destroy() {
    this.destroyed = true;
    unByKey(this.listenerKeys);
    this.listenerKeys = [];
    this.olcs.setEnabled(false);
    this.olcs.destroy();
  }
}

export default Map3D;
